/**
 * SVG Composed Overlay — Map Facade Extension
 *
 * Renders a single, pre-composed SVG asset as a geographically-positioned
 * OverlayView on Google Maps. The SVG contains all visual layers consolidated
 * into one file (lot polygons, streets, grass, amenities, labels, etc.) and is
 * positioned by geographic bounding-box corners.
 *
 * This is a single-asset approach: the backend provides one `mapBlobUrl` that
 * already has every layer merged. There is no per-layer fetching, decoding, or
 * zoom-based stacking — the SVG is used exactly as-is, inserted inline via
 * innerHTML so individual elements remain interactive.
 *
 * Event strategy: the container is mounted in overlayMouseTarget (not overlayLayer)
 * because Google Maps applies pointer-events:none to overlayLayer and its children
 * cannot receive events regardless of their own pointer-events style.
 * Lot interaction (hover, click) is handled by svgLotInteraction, and is bound
 * independently of overlay creation via the returned `bindEvents(handlers)` — this lets
 * a plugin that only cares about interaction (not rendering) wire itself up once the
 * overlay is ready, without needing to know about it at creation time.
 *
 * Contrast with svgOverlays.js, which handles multiple stacked SVGZ layers
 * loaded individually and composed at runtime — used by searchResultsMap.
 * Do not modify that module for native ISM.
 *
 * @module mapLibrary/svgComposedOverlay
 */

import { computePixelBounds, hasPixelRectChanged } from './overlayUtils';
import { applyPositionStyle } from './overlayDom';
import fetchSvgzLayerText from './svgzLoader';
import createLotInteraction from './svgLotInteraction';

/**
 * Creates an SvgComposedOverlay by composing a google.maps.OverlayView instance.
 * All state is held in closure variables.
 *
 * The consolidated SVG is fetched asynchronously inside onAdd. Google Maps calls draw()
 * immediately after onAdd (before the fetch completes) — draw() returns early when
 * mainSvgElement is null. Once the fetch resolves the SVG is inserted and draw()
 * is triggered explicitly to position it.
 *
 * @param {object} resource
 * @param {string}  resource.mapBlobUrl           - URL to the pre-composed, consolidated SVG asset
 * @param {string}  resource.sitePlanId
 * @param {number}  resource.communityId
 * @param {number}  resource.bottomLeftLatitude   - South-west corner latitude
 * @param {number}  resource.bottomLeftLongitude  - South-west corner longitude
 * @param {number}  resource.topRightLatitude     - North-east corner latitude
 * @param {number}  resource.topRightLongitude    - North-east corner longitude
 * @param {number}  [resource.rotation]
 * @param {object}  google - Loaded Google Maps API object
 * @returns {{ overlayView, onReady, bindEvents }}
 */
function buildSvgComposedOverlay(resource, google) {
  const {
    mapBlobUrl,
    rotation,
    bottomLeftLatitude,
    bottomLeftLongitude,
    topRightLatitude,
    topRightLongitude,
  } = resource;

  const state = {
    mainSvgElement:   null,
    containerElement: null,
    lastPixelRect:    null,
    lotInteraction:   null,
    isAdded:          false,
    readyCallback:    null,
    // Set to true by onRemove so an in-flight fetch does not insert elements
    // into a pane that no longer belongs to this overlay.
    cancelled:        false,
  };

  const overlayView = new google.maps.OverlayView();

  overlayView.onAdd = function () {
    const panes = overlayView.getPanes();
    state.cancelled = false;

    state.containerElement = document.createElement('div');

    fetchSvgzLayerText(mapBlobUrl).then((svgText) => {
      if (state.cancelled || !svgText) {
        return;
      }

      // Inject via innerHTML so elements are created in the live HTML document
      // context with proper CSSStyleDeclaration. The SVG is used exactly as-is.
      state.containerElement.innerHTML = svgText;
      state.mainSvgElement = state.containerElement.querySelector('svg');
      if (!state.mainSvgElement) {
        return;
      }

      // overlayMouseTarget is the pane designed for receiving pointer events.
      // overlayLayer has pointer-events:none applied by Google Maps and its
      // children cannot receive events regardless of their own pointer-events style.
      panes.overlayMouseTarget.appendChild(state.containerElement);

      // draw() returned early because mainSvgElement was null when Google Maps
      // first called it. Trigger now that the element is in the DOM.
      overlayView.draw();
    }).catch((error) => {
      console.warn('[svgComposedOverlay] Failed to load SVG:', error);
    }).finally(() => {
      state.isAdded = true;
      state.readyCallback?.(state.mainSvgElement);
      state.readyCallback = null;
    });
  };

  overlayView.draw = function () {
    if (!state.mainSvgElement) {
      return;
    }
    const projection = overlayView.getProjection();
    if (!projection) {
      return;
    }

    const southWest = projection.fromLatLngToDivPixel(
      new google.maps.LatLng(bottomLeftLatitude, bottomLeftLongitude)
    );
    const northEast = projection.fromLatLngToDivPixel(
      new google.maps.LatLng(topRightLatitude, topRightLongitude)
    );
    if (!southWest || !northEast) {
      return;
    }

    const pixelRect = computePixelBounds(southWest, northEast, rotation);
    if (!hasPixelRectChanged(state.lastPixelRect, pixelRect)) {
      return;
    }

    state.lastPixelRect = pixelRect;
    const { left, top, width, height, transform } = pixelRect;
    applyPositionStyle(state.mainSvgElement, left, top, width, height, transform);
  };

  overlayView.onRemove = function () {
    state.cancelled = true;
    state.lotInteraction?.unbind();

    if (state.containerElement?.parentNode) {
      state.containerElement.parentNode.removeChild(state.containerElement);
    }

    state.mainSvgElement   = null;
    state.containerElement = null;
    state.lastPixelRect    = null;
    state.lotInteraction   = null;
    state.isAdded          = false;
    state.readyCallback    = null;
  };

  return {
    overlayView,

    /**
     * Registers a one-time callback that fires once the SVG has loaded and the element
     * has been inserted into the DOM. If already loaded, the callback is invoked synchronously.
     */
    onReady(callback) {
      if (state.isAdded) {
        callback(state.mainSvgElement);
      } else {
        state.readyCallback = callback;
      }
    },

    /**
     * Wires (or rewires) lot hover/click interaction onto the current SVG element.
     * No-op if the SVG hasn't loaded yet — callers are expected to invoke this after
     * `onReady` has fired (directly, or via a signal derived from it).
     *
     * Passing no handlers (or a falsy value) unbinds any existing interaction without
     * rebinding — lets a plugin detach its own listeners in its own stop(), instead of
     * relying on the overlay itself being removed from the map to clean them up.
     *
     * @param {object} [handlers]
     * @param {Function} [handlers.onLotClick]
     * @param {Function} [handlers.onLotHover]
     * @param {Function} [handlers.onLotEnter]
     * @param {Function} [handlers.onLotLeave]
     */
    bindEvents(handlers) {
      if (!state.mainSvgElement) {
        return;
      }
      state.lotInteraction?.unbind();
      state.lotInteraction = null;

      if (!handlers) {
        return;
      }

      state.lotInteraction = createLotInteraction(state.mainSvgElement, handlers);
      state.lotInteraction.bind();
    },

    /**
     * Converts a point to a geographic coordinate via Google's own projection — tied to the
     * overlay's actual rendered CSS box, so it's immune to any mismatch between the SVG's
     * viewBox and its true geographic bounds (e.g. unused canvas padding/margin baked into the
     * asset), unlike interpolating against the viewBox directly. Returns null if the projection
     * isn't ready yet.
     *
     * The point is relative to the map div's CENTER, not its top-left corner — verified
     * empirically (projecting (0, 0) returns the map's current center) against the documented
     * `fromDivPixelToLatLng` contract, which claims top-left-relative. Callers must offset
     * accordingly (e.g. a lot's getBoundingClientRect() center minus the map div's own center).
     *
     * @param {{ x: number, y: number }} point
     * @returns {{ latitude: number, longitude: number } | null}
     */
    projectDivPixelToLatLng({ x, y }) {
      const projection = overlayView.getProjection();
      if (!projection) {
        return null;
      }

      const latLng = projection.fromDivPixelToLatLng(new google.maps.Point(x, y));
      if (!latLng) {
        return null;
      }

      return { latitude: latLng.lat(), longitude: latLng.lng() };
    },
  };
}

// ─── Facade extension ──────────────────────────────────────────────────────────

/**
 * Extends the map facade with SVG composed overlay support.
 *
 * Exposes:
 *   createSvgComposedOverlay(resource)
 *     → { attach, detach, onReady, bindEvents }
 *
 */
export default function svgComposedOverlay(facade, adapterState) {
  function createSvgComposedOverlay(resource) {
    const google      = adapterState.google;
    const mapInstance = adapterState.map;

    if (!google || !mapInstance) {
      return null;
    }

    const { overlayView, ...publicApi } = buildSvgComposedOverlay(resource, google);

    return {
      attach() { overlayView.setMap(mapInstance); },
      detach() { overlayView.setMap(null); },
      ...publicApi,
    };
  }

  return { ...facade, createSvgComposedOverlay };
}
